import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import { CircleX, HelpCircle } from "lucide-react";
import Dialog from "../../../../../components/shared/Dialog";
import Button from "../../../../../components/shared/Button";
import BaseForm, { Field } from "../../../../../components/shared/Form";
import { Textarea } from "../../../../../components/ui/textarea";
import useApi from "../../../../../utils/api";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { getErrorMessage } from "../../../../../utils/helpers/errors";
import { withLoadingToast } from "../../helpers/withLoadingToast";

interface UseDenyDialogProps {
    queryOptions: { queryKey: QueryKey };
    onSuccess?: () => void;
}

type DenyFormValues = {
    reason: string;
};

export const useDenyDialog = ({ queryOptions, onSuccess }: UseDenyDialogProps) => {
    const { t } = useTranslation();
    const { authPut } = useApi();
    const queryClient = useQueryClient();
    const { open, visible, close, selected } = useVisible();

    const form = useForm<DenyFormValues>({
        defaultValues: { reason: "" },
    });

    const denyMutate = useMutation({
        ...withLoadingToast(t("denying")),
        mutationFn: async ({ ids, reason }: { ids: string[]; reason: string }) => {
            return Promise.all(ids.map((id) => authPut(`/request/${id}/deny`, { data: { reason } })));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
            onSuccess?.();

            if (Array.isArray(selected?.ids) || Array.isArray(selected)) {
                toast.success(t("requests-are-confirmed"));
                selected?.refetch?.();
            } else {
                toast.success(t("request-denied-successfully"));
                selected?.close?.();
            }

            form.reset();
            close();
        },
        onError: (error) => {
            getErrorMessage(error, t);
            close();
        },
    });

    const onFinish = useCallback((value: DenyFormValues) => {
        if (Array.isArray(selected?.ids)) {
            denyMutate.mutate({ ids: selected.ids, reason: value.reason });
        } else if (Array.isArray(selected)) {
            denyMutate.mutate({ ids: selected, reason: value.reason });
        } else {
            denyMutate.mutate({ ids: [selected?.id], reason: value.reason });
        }
    }, [selected, denyMutate]);

    const onClose = useCallback(() => {
        form.reset();
        close();
    }, [close, form]);

    const dialog = useMemo(
        () => (
            <Dialog open={visible} close={onClose} size="lg">
                <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-mb-5">
                    <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-red-50 tw-flex tw-items-center tw-justify-center tw-mb-3">
                        <HelpCircle size={22} className="tw-text-red-500" />
                    </div>
                    <h2 className="tw-text-sm tw-font-semibold">{t("enter-deny-reason")}</h2>
                </div>

                <BaseForm form={form} onFinish={onFinish}>
                    <Field name="reason" label={t("reason")}>
                        <Textarea rows={5} className="tw-resize-none field-sizing-fixed" placeholder={t("reason")} />
                    </Field>

                    <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={denyMutate.isPending}>
                            <CircleX />
                            {t("cancel")}
                        </Button>

                        <Button
                            type="submit"
                            variant="destructive"
                            loading={denyMutate.isPending}
                            disabled={denyMutate.isPending}
                        >
                            {t("confirm")}
                        </Button>
                    </div>
                </BaseForm>
            </Dialog>
        ),
        [form, visible, onClose, onFinish, t, denyMutate.isPending],
    );

    return { open, dialog };
};
